import React, { useState } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import Localization from "aldoo-ra/Localization"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { showNotification } from "aldoo-ra/Notification"
import { ActivateLicense } from "aldoo-ra/Admin/ProductLicensing/api"
import Value from "aldoo-ra/Value"

const LicenseActivation = ({ onActivationSuccess }) => {
  const [licenseCode, setLicenseCode] = useState("")
  const [isActivating, setIsActivating] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const { t } = Localization()
  const texts = TextBundle({ id: "activate_license" })
  
  // Get current user's device ID from credentials
  const [userCredentials] = Value("user-credentials")

  // Clean up any existing fake device IDs from localStorage (one-time cleanup)
  React.useEffect(() => {
    // Remove the fake device ID that was incorrectly generated
    const existingDeviceId = localStorage.getItem('deviceId')
    if (existingDeviceId && existingDeviceId.startsWith('web-')) {
      localStorage.removeItem('deviceId')
    }
  }, [])

  // Handle license activation
  const handleActivation = async () => {
    // Clear previous error
    setErrorMessage("")
    
    if (!licenseCode.trim()) {
      setErrorMessage(texts?.license_code_required_error)
      return
    }

    setIsActivating(true)
    try {
      // Use current user's device ID from credentials
      const deviceId = userCredentials?.deviceID
      
      const response = await ActivateLicense({
        code: licenseCode.trim(),
        deviceId: deviceId
      })

      if (response.result) {
        showNotification({
          message: texts?.activation_success_message,
          className: "bg-success text-white"
        })
        setLicenseCode("")
        setErrorMessage("")
        // Call callback to switch to license info view
        if (onActivationSuccess) {
          onActivationSuccess()
        }
      } else {
        // Extract error message from response
        let errorMsg = texts?.license_activation_error
        
        if (response.message) {
          errorMsg = response.message
        } else if (response.error) {
          // If there's an error code but no message, use a generic message
          errorMsg = texts?.license_activation_fail
        }
        
        setErrorMessage(errorMsg)
      }
    } catch (error) {
      console.error('Error activating license:', error)
      
      // Handle different types of errors
      let errorMsg = texts?.license_activation_error
      
      if (error.response && error.response.data) {
        const responseData = error.response.data
        if (responseData.message) {
          errorMsg = responseData.message
        } else if (responseData.error) {
          errorMsg = texts?.license_activation_fail
        }
      } else if (error.message) {
        errorMsg = error.message
      }
      
      setErrorMessage(errorMsg)
    } finally {
      setIsActivating(false)
    }
  }

  // Handle enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isActivating) {
      handleActivation()
    }
  }

  // Handle input change and clear error
  const handleInputChange = (e) => {
    setLicenseCode(e.target.value)
    if (errorMessage) {
      setErrorMessage("")
    }
  }

  return (
    <div className="bg-paper rounded-3xl px-4 py-8 lg:py-10">
      <div className="max-w-xl mx-auto flex flex-col items-center">
        {/* ── Section: Activate ─────────────────────────────────────────── */}
        <Typography
          component="h2"
          variant="h3"
          className="text-center mb-6"
        >
          {texts?.activate_license_code}
        </Typography>

        <Typography 
          variant="txt_dim" 
          className="text-center mb-6"
        >
          {texts?.license_code_hint}
        </Typography>

        <div className="w-full flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <Input
            variant="snap_compact"
            value={licenseCode}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder={texts?.license_code_placeholder}
            className="flex-1"
            disabled={isActivating}
            autoFocus
          />

          <Button
            variant="primary"
            className="shrink-0"
            onClick={handleActivation}
            disabled={isActivating}
          >
            {isActivating ? texts?.license_activating_btn : texts?.license_activate_btn}
          </Button>
        </div>

        {errorMessage && (
          <Typography 
            variant="txt1" 
            className="block text-error text-center mt-2"
          >
            {errorMessage}
          </Typography>
        )}

        {/* ── Section: No license yet ───────────────────────────────────── */}
        <div className="w-full border-t border-bordered mt-8 pt-8 flex flex-col items-center gap-3">
          <Typography 
            variant="txt_dim" 
            className="text-center"
          >
            {texts?.no_license_yet}
          </Typography>

          <Button
            variant="secondary"
            onClick={() => { window.location.href = "/pricing" }}
          >
            {texts?.purchase_license_link}
          </Button>
        </div>

        {/* ── Section: After activation ─────────────────────────────────── */}
        <div className="w-full border-t border-bordered mt-8 pt-8">
          <Typography
            variant="h4"
            className="text-center mb-6"
          >
            {texts?.after_activation_note_title}
          </Typography>

          <ul className="space-y-3 xl:space-y-4 text-sm xl:text-base leading-relaxed text-text_secondary list-disc list-inside text-left">
            <li>{texts?.after_activation_list_entry_1}</li>

            <li>{texts?.after_activation_list_entry_2}</li>

            <li>{texts?.after_activation_list_entry_3}</li>

            <li>{texts?.after_activation_list_entry_4}</li>

            <li>{texts?.after_activation_list_entry_5}</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default LicenseActivation